import { FC, useCallback, useContext } from 'react';
import { Registration } from '@stax-core/types-omni';
import { coreapi } from '../../api';
import {
  AuthStore,
  RegistrationStore,
  updateAuthMerchant,
  updateRegistration,
} from '../../context';
import { history } from '../../history';
import { useNextApplicationPath, useToaster } from '../../hooks';
import { NextButton } from '../components/NextButton';
import { CenteredContent } from '../components/CenteredContent';
import { ReviewFormValues } from '../../pages/review/ReviewPage.types';
import {
  creditInquiryModalContent,
  ReviewAgreementModals,
} from './ReviewAgreementModals';
import * as businessFieldsUtil from '../../pages/business/BusinessFields.util';
import * as financialFieldsUtil from '../../pages/financial/FinancialFields.util';
import * as paymentsFieldsUtil from '../../pages/payments/PaymentsFields.util';
import { MainForm } from '../components/application/MainForm';
import envelopeGif from '../../assets/img/envelope.gif';

export const ReviewFinalPage: FC = () => {
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const {
    dispatch: authDispatch,
    state: { auth },
  } = useContext(AuthStore);
  const { toaster, toast } = useToaster();
  const nextPath = useNextApplicationPath();
  const authToken = auth?.token ?? null;

  const onSubmit = useCallback(
    async (formValues: ReviewFormValues) => {
      try {
        const signPayload = {
          credit_inquiry_electronic_signature: {
            html_content: creditInquiryModalContent,
          },
          electronic_signature: {
            user_id: auth?.user?.id,
            ...(registration?.electronic_signature || {}),
          },
        } as {
          credit_inquiry_electronic_signature: Registration['electronic_signature'];
          electronic_signature?: Registration['electronic_signature'];
        };
        //Unset the timestamp  -- allow the api to just default to now()
        if (signPayload.electronic_signature?.timestamp) {
          delete signPayload.electronic_signature?.timestamp;
        }

        const sign = await coreapi.put(
          authToken!,
          '/team/registration/sign',
          signPayload,
        );
        dispatch(updateRegistration(sign));

        // dispatch() on the previous line is asynchronous so we use the 'sign'
        // response directly for the payload mapping for the next API call instead
        // of 'formValues' or 'registration' which haven't updated yet.
        const payload: Partial<Registration> = {
          ...businessFieldsUtil.mapFormValuesToPayload(sign),
          ...financialFieldsUtil.mapFormValuesToPayload(sign),
          ...paymentsFieldsUtil.mapFormValuesToPayload(sign),
        };
        payload.application_agreed_at = new Date()
          .toISOString()
          .slice(0, 19)
          .replace('T', ' ');

        if (sign?.meta) {
          payload.meta = {
            ...sign.meta,
            ...payload.meta,
          };
        }
        delete payload['files'];

        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));

        // same as above, the previous API call and dispatch() doesn't update
        // registration synchronously so we build the next request payload based
        // on the response from the last request.
        const teamPayload = {
          ...businessFieldsUtil.mapFormValuesToTeamPayload(r),
          ...financialFieldsUtil.mapFormValuesToPayload(r),
        };

        const m = await coreapi.put(authToken!, '/team', teamPayload);
        authDispatch(updateAuthMerchant(m));

        history.push(nextPath!);
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [
      registration,
      auth?.user?.id,
      authToken,
      dispatch,
      authDispatch,
      nextPath,
      toaster,
      toast,
    ],
  );

  return (
    <>
      <MainForm initialValues={registration} onSubmit={onSubmit}>
        {formProps => (
          <CenteredContent
            id="review-final"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 100 }}
            exit={{ opacity: 0, y: -20 }}
            $gridTemplateRows={'1fr auto auto auto 1fr auto'}
          >
            <div>{/** Placeholder for back button so CSS works */}</div>
            <h2 style={{ textAlign: 'center' }}>Submit application</h2>
            <img
              style={{
                width: '200px',
                marginRight: 'auto',
                marginLeft: 'auto',
              }}
              src={envelopeGif}
              alt={'Invite sent'}
            />
            <p>Review the information below and submit when ready.</p>
            <div>{/** Placeholder for space to match figma design */}</div>
            <div>
              <ReviewAgreementModals formValues={formProps.values} />
              <NextButton
                id="review-final-submit"
                text="Submit"
                formProps={formProps}
              />
            </div>
          </CenteredContent>
        )}
      </MainForm>
    </>
  );
};
